import { trackConfig, type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface IWritingMode {
    /**
     * 设置文字方向
     *
     * @param layerID - 图层 ID
     * @param writingMode - 写入模式
     */
    setWritingMode(layerID: string, writingMode: TextWritingType): ILayerProps | void;
}

export class WritingMode implements IWritingMode {
    constructor(private framework: IFramework) {}

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo as VectorTextInfo;

        const { writingMode } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
        });

        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    setWritingMode(layerID: string, writingMode: TextWritingType) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = prevLayerData;

        if (!textInfo || textInfo.writingMode === writingMode) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, writingMode };
        const nextLayerData: IVectorLayerProps = { ...prevLayerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].writingMode);

        return nextLayer;
    }
}
